import { MenuItem, Stack } from '@mui/material';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Sector } from '@/domain/impact/impact.entity';
import {
  Category,
  ImpactCo2Answer,
  MealTypeValues,
  ValuesCateringMeal,
} from '@/domain/impact-co2/entities/question.entity';
import { selectImpactCo2Answers } from '@/domain/impact-co2/impact-co2.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import {
  AddNewQuiz,
  NewQuizEdition,
  QuizEdition,
  QuizNotEdition,
} from '@/ui/pages/app/components/quiz-panel/co2/components/QuizComponents';
import TagForm from '@/ui/pages/app/components/quiz-panel/co2/components/TagForm.tsx';
import { isNumber } from '@/ui/utils/form-validations';

import { ImpactQuestionContext } from '../../../ImpactQuestionContext';
import { useImpactCo2AnswerSubmitDisabled } from '../../hooks/useImpactCo2AnswerSubmitDisabled';

const CATEGORY: Category = 'CATERING_MEAL';

export function CateringMealForm({
  sector,
  onSubmit,
  onDelete,
  onEdit,
  isExternal = false,
}: {
  sector: Sector;
  onSubmit(answer: ImpactCo2Answer): void;
  onDelete(answer: ImpactCo2Answer): void;
  onEdit(answer: ImpactCo2Answer): void;
  isExternal?: boolean;
}) {
  const { hideCompleted, panelOpen, answersSelectorOverride } = useContext(
    ImpactQuestionContext,
  );
  const [displayNew, setDisplayNew] = useState(false);
  const [displayTag, setDisplayTag] = useState(false);
  const [editing, setEditing] = useState<Array<number>>([]);

  const defaultAnswersSelector = selectImpactCo2Answers<ValuesCateringMeal>({
    sector: sector,
    category: CATEGORY,
    isExternal: isExternal,
  });
  let answersSelector = defaultAnswersSelector;
  if (answersSelectorOverride) {
    answersSelector = answersSelectorOverride;
  }
  const answers: ImpactCo2Answer<ValuesCateringMeal>[] =
    useAppSelector(answersSelector);

  const { disableSubmitActions } = useImpactCo2AnswerSubmitDisabled();

  const handleSubmit = (values: ValuesCateringMeal) => {
    const answer: ImpactCo2Answer = {
      id: -1,
      userId: '',
      sector: sector,
      category: CATEGORY,
      values: values,
      isExternal: isExternal,
      source: '',
    };
    onSubmit(answer);
    setDisplayNew(false);
  };

  const handleEdit = (values: ValuesCateringMeal, id: number) => {
    const answer: ImpactCo2Answer = {
      id,
      userId: '',
      sector,
      category: CATEGORY,
      values,
      isExternal: isExternal,
      source: '',
    };
    onEdit(answer);
    removeFromEditing(answer.id);
  };

  const handleDelete = () => {
    setDisplayNew(false);
  };

  const addToEditing = (id: number) => {
    setEditing([...editing, id]);
  };

  const removeFromEditing = (id: number) => {
    setEditing([...editing].filter((i) => i !== id));
  };

  return (
    <>
      {/* check if we have to hide question if already completed */}
      {!(hideCompleted && answers.length > 0) && (
        <>
          {/* already created answers */}
          {answers
            .sort((a, b) => a.id - b.id)
            .map((answer, i) => (
              <div key={`catering-meal` + i}>
                {editing.includes(answer.id) && !!panelOpen ? (
                  <QuizEdition
                    answer={answer}
                    removeFromEditing={removeFromEditing}
                    index={i}
                    setDisplayTag={() => setDisplayTag(!displayTag)}
                    title={translate(
                      'ImpactQuestion.CATERING_MEAL.question-title',
                    )}
                    form={
                      <Form
                        onSubmit={(values) => handleEdit(values, answer.id)}
                        defaultValues={answer.values}
                        onDelete={() => onDelete(answer)}
                        answersCount={answers.length}
                        disableSubmit={disableSubmitActions}
                        disableDelete={disableSubmitActions}
                        isExternal={isExternal}
                        displayTag={displayTag}
                      />
                    }
                  />
                ) : (
                  <QuizNotEdition
                    addToEditing={addToEditing}
                    answeredValues={[
                      translate(
                        `ImpactQuestion.CATERING_MEAL.mealTypeValues.${answer.values.mealType}`,
                      ),
                      `${answer.values.mealCount} ${translate(
                        'ImpactQuestion.CATERING_MEAL.meal',
                      )}`,
                    ]}
                    title={translate(
                      'ImpactQuestion.CATERING_MEAL.question-title',
                    )}
                    index={i}
                    answer={answer}
                    quizLabel={answer.values.label}
                    score={answer.values.score}
                  />
                )}
              </div>
            ))}

          {/* new answer */}
          {displayNew || answers.length === 0 ? (
            <NewQuizEdition
              answersLength={answers.length}
              title={translate('ImpactQuestion.CATERING_MEAL.question-title')}
              setDisplayTag={() => setDisplayTag(!displayTag)}
              form={
                <Form
                  onSubmit={handleSubmit}
                  onDelete={() => handleDelete()}
                  answersCount={answers.length}
                  disableSubmit={disableSubmitActions}
                  disableDelete={disableSubmitActions}
                  isExternal={isExternal}
                  displayTag={displayTag}
                />
              }
            />
          ) : (
            <AddNewQuiz
              setDisplayNew={setDisplayNew}
              buttonTitle={translate(
                'ImpactQuestion.CATERING_MEAL.add-button-title',
              )}
            />
          )}
        </>
      )}
    </>
  );
}

function Form({
  onSubmit,
  onDelete,
  defaultValues,
  answersCount,
  disableSubmit,
  disableDelete,
  isExternal,
  displayTag,
}: {
  onSubmit: (values: ValuesCateringMeal) => void;
  onDelete: () => void;
  defaultValues?: ValuesCateringMeal;
  answersCount: number;
  disableSubmit: boolean;
  disableDelete: boolean;
  displayTag: boolean;
  isExternal?: boolean;
}) {
  const initialValues: ValuesCateringMeal = defaultValues || {
    mealType: 'AVERAGE_MEAL',
    mealCount: 0,
    label: '',
    score: 0,
    tags: [],
  };

  const { handleSubmit, register, formState, setValue } =
    useForm<ValuesCateringMeal>({
      defaultValues: initialValues,
    });

  const { errors } = formState;

  const handleFormSubmit = (data: ValuesCateringMeal) => {
    const values = {
      ...data,
      mealCount: parseInt(data.mealCount.toString()),
    };
    onSubmit(values);
  };

  return (
    <Stack>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack>
          {!isExternal && (displayTag || initialValues.tags.length > 0) && (
            <TagForm
              handleChange={(tags) => setValue('tags', tags)}
              defaultValue={initialValues.tags}
            />
          )}
          <FSTextField
            type="text"
            defaultValue={initialValues.label}
            register={{
              ...register('label'),
            }}
            error={!!errors.label}
            helperText={errors.label?.message}
            placeholder={translate('ImpactQuestion.add-title')}
            placeholderColor="#A29BAE"
            variant="standard"
            sx={{ mb: 2 }}
          />
          <FSTextField
            select
            defaultValue={initialValues.mealType}
            label={translate('ImpactQuestion.CATERING_MEAL.mealTypeTitle')}
            register={{
              ...register('mealType'),
            }}
            error={!!errors.mealType}
            helperText={errors.mealType?.message}
          >
            {MealTypeValues.map((value) => (
              <MenuItem key={value} value={value}>
                {translate(
                  `ImpactQuestion.CATERING_MEAL.mealTypeValues.${value}`,
                )}
              </MenuItem>
            ))}
          </FSTextField>

          <FSTextField
            type="number"
            label={translate('ImpactQuestion.CATERING_MEAL.mealCountTitle')}
            defaultValue={initialValues.mealCount}
            register={{
              ...register('mealCount', {
                required: translate('Form.fill-the-field'),
                validate: isNumber,
              }),
            }}
            error={!!errors.mealCount}
            helperText={errors.mealCount?.message}
          />

          <Stack direction={'row'} marginTop={1} gap={1}>
            <FSPrimaryButton type="submit" disabled={disableSubmit}>
              {translate(defaultValues ? 'Form.save' : 'Form.add')}
            </FSPrimaryButton>
            {onDelete && answersCount > 0 && (
              <FSSecondaryButton onClick={onDelete} disabled={disableDelete}>
                {translate('Form.delete')}
              </FSSecondaryButton>
            )}
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
